/**
 * FRONT-2594 — the Canvas item model: which documents, sketches and decks a
 * chat has produced, derived purely from its message list.
 *
 * Sources are COMPLETED, SUCCESSFUL tool parts only. Tool part shape is the
 * AI SDK's (`dynamic-tool` with `toolName`, or `tool-<name>`; `state`,
 * `output`) — the same one `getToolInfo` in chat/message-bubble.tsx reads.
 * `output` arrives as an already-parsed object (message-bubble casts it
 * straight to the card output types); a JSON string is tolerated too.
 *
 * TOOL NAMES ARE NOT UNIQUE: the sandbox re-registers `file_write` with a
 * different result shape, so a document is recognised by its RESULT SHAPE
 * (`saved: true`, `kind: "document"`, string `filename`), never the name alone.
 */

export type CanvasItemKind = 'document' | 'sketch' | 'deck' | 'file';

export interface CanvasItem {
  /** `document:<filename>` | `sketch:<slug>` | `deck:<slug>` | `file:<fileId>` */
  key: string;
  kind: CanvasItemKind;
  /** filename (document), slug (sketch / deck) or file id (file). */
  id: string;
  title: string;
  /** From the latest successful output; null when the tool result carries none (documents). */
  updatedAt: string | null;
  /** Count of successful outputs for this key. */
  revision: number;
  /**
   * The tool-call id of every successful output for this key, in order. Auto-open
   * detects a change as "an id never seen before for this item" — a COUNT of
   * outputs cannot, because deleting a message lowers it and the next write then
   * lands on a number already seen. A part without a `toolCallId` gets the
   * stand-in `rev:<n>`, which degrades to count behaviour for that part only.
   */
  callIds: string[];
  /**
   * The subset of `callIds` whose output was an EXPLICIT request to show the
   * item (FRONT-2669): the output carried `canvas.open === true`, or came from a
   * tool that always means "show this" (`sketch_show`, `deck_publish`,
   * `artifact_document_write`, `artifact_restore_previous`). A titled
   * `file_write` document or a Markdown `outputFiles` entry is collected (the
   * switcher, the card's own button) but never explicit, so it can never open
   * the panel on its own.
   */
  explicitCallIds: string[];
  /** True when this item's LATEST successful output was explicit. */
  latestExplicit: boolean;
  /**
   * Position of this key's LATEST successful output across the whole message
   * list (a running counter over every Canvas-relevant output). Auto-open uses
   * it to pick the item produced most recently, which `items` order (first
   * appearance) cannot say.
   */
  seq: number;
  /** A sketch's served path from the tool output (`sketch.path`), else null; callers fall back to `/apps/<slug>/`. */
  path: string | null;
  /**
   * A sketch's / deck's absolute served URL from the tool output (`<kind>.url`,
   * `${origin}${path}`), else null. Lets a link in reply text on the apps host
   * be recognised as this item (FRONT-2744).
   */
  url: string | null;
  /** A document write's save warnings (empty for sketches / decks). */
  warnings: string[];
  /** Where a document write was auto-filed (a Work container), else null. */
  attachedTo: { name: string; kind: string } | null;
}

interface LooseMessage {
  parts?: unknown[];
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function parseOutput(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'string') {
    try {
      return asRecord(JSON.parse(raw));
    } catch {
      return null;
    }
  }
  return asRecord(raw);
}

function nonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function toolNameOf(part: Record<string, unknown>): string | null {
  const type = part.type;
  if (typeof type !== 'string') return null;
  if (type === 'dynamic-tool') return nonEmptyString(part.toolName);
  return type.startsWith('tool-') ? type.slice('tool-'.length) : null;
}

/** `title` null = the output carries none; the item keeps the title it already has (else its id). */
type Candidate = Omit<CanvasItem, 'revision' | 'seq' | 'callIds' | 'explicitCallIds' | 'latestExplicit' | 'title'> & {
  title: string | null;
  explicit: boolean;
};

function documentCandidate(out: Record<string, unknown>): Candidate | null {
  if (out.saved !== true || out.kind !== 'document') return null;
  const filename = nonEmptyString(out.filename);
  if (!filename) return null;
  return {
    key: `document:${filename}`,
    kind: 'document',
    id: filename,
    title: nonEmptyString(out.title),
    updatedAt: nonEmptyString(out.updatedAt),
    path: null,
    url: null,
    warnings: Array.isArray(out.warnings) ? out.warnings.filter((w): w is string => typeof w === 'string') : [],
    attachedTo: attachedToOf(out.attachedTo),
    explicit: false,
  };
}

function attachedToOf(v: unknown): { name: string; kind: string } | null {
  const r = asRecord(v);
  const name = r ? nonEmptyString(r.name) : null;
  return r && name ? { name, kind: nonEmptyString(r.kind) ?? '' } : null;
}

/**
 * The document a `file_write` result describes, or null when it is any other
 * shape (the sandbox's own `file_write`, an error). Shared by the chat card so
 * the card and the item list can never disagree about what a document write is.
 */
export function documentWriteOf(
  output: unknown,
): { filename: string; title: string; warnings: string[]; attachedTo: { name: string; kind: string } | null } | null {
  const out = parseOutput(output);
  const c = out ? documentCandidate(out) : null;
  return c ? { filename: c.id, title: c.title ?? c.id, warnings: c.warnings, attachedTo: c.attachedTo } : null;
}

function appCandidate(
  kind: 'sketch' | 'deck',
  out: Record<string, unknown>,
): Candidate | null {
  if (out.ok !== true) return null;
  const row = asRecord(out[kind]);
  const slug = row ? nonEmptyString(row.slug) : null;
  if (!row || !slug) return null;
  return {
    key: `${kind}:${slug}`,
    kind,
    id: slug,
    title: nonEmptyString(row.title),
    updatedAt: nonEmptyString(row.updatedAt),
    path: kind === 'sketch' ? nonEmptyString(row.path) : null,
    url: nonEmptyString(row.url),
    warnings: [],
    attachedTo: null,
    explicit: false,
  };
}

/**
 * True for a Markdown file: its content type says so, or — when the type is
 * missing or generic — its name ends `.md` / `.markdown` (any case).
 */
export function isMarkdownOutputFile(f: { filename: string; contentType?: string }): boolean {
  const ct = (f.contentType ?? '').split(';')[0]!.trim().toLowerCase();
  return ct === 'text/markdown' || /\.(md|markdown)$/i.test(f.filename);
}

/**
 * Every PERSISTED Markdown file a tool result lists in `outputFiles`, from ANY
 * tool: `file_write`'s plain-file mode, the sandbox's `file_write` and
 * `terminal_exec` collection, `sandbox_save_file` and custom tools all report a
 * saved file in this one shape ({filename, fileId, url, sizeBytes,
 * contentType}). An entry with no `fileId` was not saved (inline trial run,
 * skipped, too large) and has nothing to open. Title is the filename; the
 * output carries no ISO updatedAt for a file (a `file_write` result's
 * `updatedAt` is a localised display string — never read here).
 */
function fileCandidates(out: Record<string, unknown>): Candidate[] {
  if (!Array.isArray(out.outputFiles)) return [];
  const found: Candidate[] = [];
  for (const raw of out.outputFiles) {
    const f = asRecord(raw);
    const fileId = f ? nonEmptyString(f.fileId) : null;
    const filename = f ? nonEmptyString(f.filename) : null;
    if (!f || !fileId || !filename) continue;
    if (!isMarkdownOutputFile({ filename, contentType: typeof f.contentType === 'string' ? f.contentType : undefined })) continue;
    found.push({
      key: `file:${fileId}`,
      kind: 'file',
      id: fileId,
      title: filename,
      updatedAt: null,
      path: null,
      url: null,
      warnings: [],
      attachedTo: attachedToOf(out.attachedTo),
      explicit: false,
    });
  }
  return found;
}

function candidatesOf(name: string, out: Record<string, unknown>, input: Record<string, unknown> | null): Candidate[] {
  const own = ownCandidate(name, out, input);
  return own ? [own, ...fileCandidates(out)] : fileCandidates(out);
}

/**
 * FRONT-2600's two in-place revision tools (src/artifacts/tool.ts). Eddy revising
 * the OPEN document must grow that document's own item, so both key to
 * `document:<filename>` exactly like `file_write`:
 *  - `artifact_document_write` returns `{ saved: true, kind: "document", filename,
 *    updatedAt, currentUpdatedAt?, note? }` — the document shape, matched as one.
 *  - `artifact_restore_previous` returns only `{ ok: true, updatedAt, ... }` with
 *    NO filename (it carries the restored `title`), so the document is read from the call's own input (`id`, with
 *    `kind: "document"`). A refusal (`{ error }`, `ok` absent) is not a change.
 */
function restoreCandidate(out: Record<string, unknown>, input: Record<string, unknown> | null): Candidate | null {
  if (out.ok !== true || !input || input.kind !== 'document') return null;
  const id = typeof input.id === 'string' ? input.id.trim() : '';
  if (!id) return null;
  return {
    key: `document:${id}`,
    kind: 'document',
    id,
    // The restore result names the restored title (the document may have been renamed since).
    title: nonEmptyString(out.title),
    updatedAt: nonEmptyString(out.updatedAt),
    path: null,
    url: null,
    warnings: [],
    attachedTo: null,
    explicit: false,
  };
}

/** Tools whose every success means "show this beside the chat", with or without a `canvas` marker. */
const ALWAYS_EXPLICIT_TOOLS = new Set(['sketch_show', 'deck_publish', 'artifact_document_write', 'artifact_restore_previous']);

/** The backend's own request to open the panel: `canvas: { open: true, ... }` on the result. */
function canvasOpenRequested(out: Record<string, unknown>): boolean {
  return asRecord(out.canvas)?.open === true;
}

/**
 * FRONT-2669's three tools (src/artifacts/tool.ts). `artifact_document_create` and
 * `artifact_document_edit` return the document shape; `artifact_open` returns the
 * document shape for a document, or the `sketch` / `deck` row exactly as
 * `sketch_show` / `deck_publish` do, so the item is identical to theirs.
 */
function artifactToolCandidate(name: string, out: Record<string, unknown>): Candidate | null {
  if (name === 'artifact_document_create' || name === 'artifact_document_edit') return documentCandidate(out);
  if (name !== 'artifact_open' || out.ok !== true) return null;
  const kind = asRecord(out.canvas)?.kind;
  if (kind === 'sketch' || kind === 'deck') return appCandidate(kind, out);
  return documentCandidate(out);
}

function rawOwnCandidate(name: string, out: Record<string, unknown>, input: Record<string, unknown> | null): Candidate | null {
  if (name === 'artifact_document_write') return documentCandidate(out);
  if (name === 'artifact_restore_previous') return restoreCandidate(out, input);
  if (name === 'sketch_show') return appCandidate('sketch', out);
  if (name === 'deck_publish') return appCandidate('deck', out);
  if (name.startsWith('artifact_')) return artifactToolCandidate(name, out);
  // `file_write` is the only tool whose result is a document write; matched
  // by name AND shape (see header). `file_read` / `file_list` also carry
  // `kind: "document"` but never `saved: true`.
  if (name === 'file_write') return documentCandidate(out);
  return null;
}

function ownCandidate(name: string, out: Record<string, unknown>, input: Record<string, unknown> | null): Candidate | null {
  const c = rawOwnCandidate(name, out, input);
  if (!c) return null;
  return { ...c, explicit: ALWAYS_EXPLICIT_TOOLS.has(name) || canvasOpenRequested(out) };
}

/** Ordered by first appearance; title / updatedAt come from the latest successful output. */
export function collectCanvasItems(messages: readonly LooseMessage[] | null | undefined): CanvasItem[] {
  const byKey = new Map<string, CanvasItem>();
  let seq = 0;
  for (const message of messages ?? []) {
    for (const raw of message?.parts ?? []) {
      const part = asRecord(raw);
      if (!part || part.state !== 'output-available') continue;
      const name = toolNameOf(part);
      if (!name) continue;
      const out = parseOutput(part.output);
      if (!out) continue;
      for (const c of candidatesOf(name, out, parseOutput(part.input))) {
        const prev = byKey.get(c.key);
        const revision = (prev?.revision ?? 0) + 1;
        const callId = nonEmptyString(part.toolCallId) ?? `rev:${revision}`;
        const { explicit, ...fields } = c;
        byKey.set(c.key, {
          ...fields,
          title: c.title ?? prev?.title ?? c.id,
          revision,
          seq: seq++,
          callIds: [...(prev?.callIds ?? []), callId],
          explicitCallIds: [...(prev?.explicitCallIds ?? []), ...(explicit ? [callId] : [])],
          latestExplicit: explicit,
        });
      }
    }
  }
  return [...byKey.values()];
}
